import downloadIconBlack from "@/assets/img/icon-download-black.svg";
import BillingDataForm from "@/components/org-management/BillingDataForm.component";
import { Loader } from "@/components/shared/Loader.component";
import { SelectedDataExtractor } from "@/components/shared/table/selected-data-extractor";
import { TableEditingState } from "@/components/shared/table/table-editing-state";
import { TableSelectionState } from "@/components/shared/table/table-selection-state";
import Table from "@/components/shared/table/Table.component";
import { ITableField } from "@/components/shared/table/types";
import { IQueryListInvoicesByBillingAccountArgs } from "@/generated/types";
import { BContainer, BRow, BSpinner } from "@/lib/typed-bootstrap";
import UserPermissions from "@/mixins/UserPermissions.mixin";
import { ComplexFieldTypeEnum } from "@/models";
import { IInvoiceItem, watchInvoicesByBillingAccount } from "@/models/invoice";
import { OrgModule } from "@/store/modules/org";
import { ListDataManager } from "@/utils/list-data-manager";
import Vue from "vue";
import { mixins } from "vue-class-component";
import { Component, Watch } from "vue-property-decorator";
import * as tsx from "vue-tsx-support";

@Component
export default class OrgBillingDashboard extends mixins(UserPermissions) {
  _tsx!: tsx.DeclareProps<{}> & tsx.DeclareOnEvents<{}>;

  private get fields(): ITableField[] {
    return [
      {
        key: "date",
        label: this.$t("org_billing_table_header_date").toString(),
      },
      {
        key: "currency",
        label: this.$t("org_billing_table_header_currency").toString(),
      },
      {
        key: "amountPaid",
        label: this.$t("org_billing_table_header_amount").toString(),
      },
      {
        key: "status",
        label: this.$t("org_billing_table_header_status").toString(),
      },
      {
        key: "invoicePdf",
        label: this.$t("org_billing_table_header_invoice").toString(),
        download: true,
        type: ComplexFieldTypeEnum.Url,
        iconSrc: this.downloadIconSrc,
      },
    ];
  }

  private downloadIconSrc = downloadIconBlack;

  private listDataManager = Vue.observable(
    new ListDataManager<
      { items: IInvoiceItem[]; nextToken?: string | null },
      IQueryListInvoicesByBillingAccountArgs
    >(watchInvoicesByBillingAccount, {
      perPage: 5,
      pageSizes: [5, 20],
      currentPage: 1,
    }),
  );

  private get items() {
    return this.listDataManager.data;
  }

  private selectedDataExtractor = new SelectedDataExtractor(
    [],
    [],
    false,
    false,
  );

  private get tableSelectionState() {
    return new TableSelectionState(true, false);
  }

  private get tableEditingState() {
    return Vue.observable(new TableEditingState(this.fields));
  }

  @Watch("orgId", { immediate: true })
  onOrgIdChange() {
    this.listDataManager.subscribeToList({
      billingAccountPaymentProviderId:
        OrgModule.billingAccount?.paymentProviderId ?? "",
    });
  }

  render() {
    return (
      <BContainer class="default__view-setup">
        <BillingDataForm
          scopedSlots={{
            "form-title": () => (
              <BRow class="default__section">
                <div class="default__section-header">
                  {this.$t("org_data_address_header").toString()}
                </div>
              </BRow>
            ),
            "form-buttons": (formProps) =>
              formProps.showButtons ? (
                <BRow class="buttons__section">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      formProps.onCancel();
                    }}
                    class="action-button__pill--outline action-button__flex-props"
                    type="reset"
                  >
                    {this.$t("global_cta_cancel").toString()}
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      formProps.onSave(e);
                    }}
                    class="action-button__pill--gradient action-button__flex-props"
                    data-testid="save-button"
                    type="submit"
                    disabled={formProps.isLoading}
                  >
                    {formProps.isLoading ? (
                      <div
                        class="default__spinner--small"
                        data-testid="loading-spinner"
                        role="status"
                      >
                        <BSpinner
                          type="grow"
                          class="ml-auto"
                          label="Saving..."
                          small
                        />
                      </div>
                    ) : null}
                    {this.$t("global_cta_save").toString()}
                  </button>
                </BRow>
              ) : null,
          }}
        />

        <BRow class="default__section">
          <div class="default__section-header">
            {this.$t("org_billing_title").toString()}
            <span class="default__color-accent">
              {` |`} {this.listDataManager.data.length}{" "}
              {this.$t("global_total").toString()}
            </span>
          </div>
        </BRow>
        <BRow class="default__sub-section">
          <Table
            items={this.items}
            fields={this.fields}
            onPaginationChange={this.listDataManager.onPaginationChange}
            paginationState={this.listDataManager.paginationState}
            hasMoreItems={!!this.listDataManager.listVariables?.nextToken}
            tableSelectionState={this.tableSelectionState}
            tableEditingState={this.tableEditingState}
            selectedDataExtractor={this.selectedDataExtractor}
            withSelectionColumn
          >
            {this.listDataManager.isLoading ? (
              <Loader data-testid="tableLoader" />
            ) : null}
          </Table>
        </BRow>
      </BContainer>
    );
  }
}
